import type { GraphQLFormattedError } from 'graphql';
import type { ReactNode } from 'react';

import { useQueryParam } from 'src/app/shared/hooks/useQueryParam';
import { log } from 'src/app/utils/logging';
import { ErrorPageRecoverable } from 'src/app/v2/pages/ErrorPageRecoverable';
import { ErrorPageUnrecoverable } from 'src/app/v2/pages/ErrorPageUnrecoverable';
import { useDraftJobQuery } from 'src/graphql/generated';

interface ProviderProps {
  children: ReactNode;
}

export const DraftLoader = ({ children }: ProviderProps) => {
  const draftId = useQueryParam('draftid');
  const cartId = useQueryParam('cartid');

  // Reason to fallback to cartId:
  // The draftId is used to set as cartId during cart init phase.
  // When return from 3rd payment site to our payAndPost page,
  // both cartId and draftId should be in the url queryParams.
  // But sometime draftId will be missing in queryParams.
  // So fallback to cartId.
  const draftOrCartId = draftId || cartId;

  const { data, error } = useDraftJobQuery({
    variables: {
      id: draftOrCartId!,
    },
    skip: !draftOrCartId,
    fetchPolicy: 'cache-and-network',
  });

  if (!draftOrCartId) {
    log.error('DraftLoader | Both draftId and cartId are missing');
    return <ErrorPageUnrecoverable />;
  }

  if (error) {
    log.error('DraftLoader | Error fetching draft', { error });

    if (
      error.graphQLErrors?.find(
        (graphQLError: GraphQLFormattedError) =>
          graphQLError.extensions?.code === 'DRAFT_CLOSED_OR_DELETED',
      )
    ) {
      return <ErrorPageUnrecoverable />;
    }
  }

  if (error && !data) {
    return <ErrorPageRecoverable />;
  }

  if (!data) {
    return null;
  }

  if (data.draftJob?.__typename === 'DraftJobNotFoundPayload') {
    log.error('DraftLoader | DraftJobNotFoundPayload', {
      draftId: draftOrCartId,
    });
    return <ErrorPageUnrecoverable />;
  }

  return <>{children}</>;
};
